import { useEffect, useId, useState } from 'react';
import { usersService, type User } from '@/services/users.service';
import { cn } from '@/utils/cn';

export interface UserPickerProps {
  value: string;
  onChange: (user: { id: string; name: string; email: string } | null) => void;
  label: string;
  roleFilter?: string[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Shared user picker. Renders a labelled <select> populated by the
 * users-service. When the user changes, emits the full {id, name, email}
 * object (or null on placeholder). Optional roleFilter restricts the list
 * to users holding one of the listed role names.
 */
export function UserPicker({
  value,
  onChange,
  label,
  roleFilter,
  disabled,
  placeholder = '—',
  className,
}: UserPickerProps) {
  const id = useId();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    usersService
      .getUsers({ limit: 200, isActive: true })
      .then((res) => {
        if (!alive) {
          return;
        }
        const all = res.users;
        const filtered = roleFilter
          ? all.filter((u) => {
              const userRoles = (u as User & { roles?: unknown }).roles;
              if (!Array.isArray(userRoles)) {
                return false;
              }
              return userRoles.some((r) =>
                roleFilter.includes(
                  typeof r === 'string' ? r : ((r as { name?: string }).name ?? ''),
                ),
              );
            })
          : all;
        setUsers(filtered);
      })
      .catch(() => {
        if (alive) {
          setUsers([]);
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [JSON.stringify(roleFilter)]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cn('space-y-1', className)}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={(disabled ?? false) || loading}
        onChange={(e) => {
          const selectedId = e.target.value;
          if (!selectedId) {
            onChange(null);
            return;
          }
          const u = users.find((x) => x.id === selectedId);
          if (!u) {
            return;
          }
          const name =
            u.fullName ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
          onChange({ id: u.id, name, email: u.email });
        }}
        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
      >
        <option value="">{placeholder}</option>
        {loading ? (
          <option disabled>Loading users...</option>
        ) : (
          users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.fullName ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()}
            </option>
          ))
        )}
      </select>
    </div>
  );
}
